/*
 * File: Topbar.styles.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 7, 2022 at 1:52 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 5:34 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import styled, { css } from 'styled-components';

// Components
import { IconButton } from '../../IconButton';
import { LocaleLink } from '../../LocaleLink';
import { Padding } from '../../Padding';

// Constants
import {
	AppNavTopbarHeight, AppNavTopbarMobileSegmentHeight, buttonCopyBreakpoint, tabBarBreakpoint
} from '../../../styles/constants';


/**
 * Styles
 */

export const Topbar = styled.div`
	display: flex;
	background-color: ${({ theme }) => theme.primaryBackground};
	z-index: 1000;
	pointer-events: all;
	top: 0px !important;
	width: 100%;
	border-bottom: 1px solid ${({ theme }) => theme.layoutBorder};

	@media only screen and (min-width: ${tabBarBreakpoint}em) {
		border: none;
	}
	${({ $isFixed }) => $isFixed && css`
		position: fixed !important;
	`}
	${({ $isScrolled, $isFixed, $isTransparent }) => $isScrolled && $isFixed && $isTransparent && css`
		background-color: ${({ theme }) => theme.primaryBackground};
	`}
	${({ $isScrolled, $isFixed, theme }) => ($isScrolled && $isFixed ? css`
		box-shadow: ${theme.navigationShadow};
	` : css`
		box-shadow: none;
	`)};

	button {
		pointer-events: auto;
		align-items: center;
		display: flex;
		justify-content: center;

		svg {
			font-size: 0.9rem;
		}
		span {
			align-items: center;
			display: flex;
			justify-content: center;
		}
		* {
			margin: 0px;
		}
	}
`;

export const TopbarPadding = styled(Padding)`
	flex-direction: column;
`;

export const TopbarPrimarySegment = styled.div`
	width: 100%;
	display: flex;
	height: ${AppNavTopbarHeight}px;
	min-height: ${AppNavTopbarHeight}px;
	padding: 0.78rem 0;

	* :not(svg,path) {
		height: 100%;
	}
`;

export const TopbarMobileSegment = styled.div`
	width: 100%;
	display: flex;
	height: ${AppNavTopbarMobileSegmentHeight}px;
	min-height: ${AppNavTopbarMobileSegmentHeight}px;
	margin-top: -0.3rem;

	* :not(svg,path) {
		height: 100%;
	}
	&.condensedNavigation {
		display: none !important;

		@media only screen and (max-width: ${tabBarBreakpoint}em) {
			display: inherit !important;
		}
	}
`;

export const TopbarWrapper = styled.div`
	display: flex;
	align-items: center;

	${({ $fullWidth }) => $fullWidth && css`
		justify-content: center;
		flex: 1;
	`}
	.logoImage {
		img {
			padding: 0.2rem 0px 0.2rem 0;
		}
	}
`;

export const ButtonContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;

	button {
		pointer-events: auto;
		align-items: center;
		display: flex;
		justify-content: center;

		svg {
			font-size: 0.9rem;
		}
		span {
			align-items: center;
			display: flex;
			justify-content: center;
		}
		* {
			margin: 0px;
		}
	}
	.condensedNavigation {
		display: none !important;

		@media only screen and (max-width: ${buttonCopyBreakpoint}em) {
			display: inherit !important;
		}
	}
	.expandedNavigation {
		display: inherit !important;

		@media only screen and (max-width: ${buttonCopyBreakpoint}em) {
			display: none !important;
		}
	}
`;

export const NavigationToggle = styled(IconButton)`
	height: auto;
	margin: 0px 14px 0px 0px !important;
	color: ${({
		theme
	}) => theme.primaryText};

	&.condensedNavigation {
		display: none !important;

		@media only screen and (max-width: ${tabBarBreakpoint}em) {
			display: inherit !important;
		}
	}
`;

export const LinkContainer = styled.div`
	height: 100%;
	display: flex;
	align-items: center;
	gap: 40px;
	margin-left: 40px;
`;

export const NavigationLink = styled(LocaleLink)`
	height: auto !important;
	text-decoration: none;
	color: ${({
		theme, $isScrolled
	}) => (!$isScrolled ? theme.white : theme.primaryText)};

	svg {
		font-size: 0.8rem;
		margin: 0px 0px 0px 6px;
		position: relative;
		top: 1px;
		transform-box: fill-box;
  		transform-origin: center;
		transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
	}
`;

export const ActionContainer = styled.div``;

export const NavigationTabContainer = styled.div`
	height: ${({ $height }) => $height}px !important;
	display: grid;
	grid-gap: 20px;
	grid-template-columns: repeat(3, 1fr);
	width: 100%;
	justify-content: center;

	& div {
		display: flex;
	}
	&.expandedNavigation {
		display: inherit !important;

		@media only screen and (max-width: ${tabBarBreakpoint}em) {
			display: none !important;
		}
	}
`;

export const ImpersonationIndicator = styled.div`
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 15px;

	span {
		height: auto;
	}
`;
